import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Grid,
  Popover,
  Link,
  Chip
} from '@mui/material';
import { Redeem as RedeemIcon, MonetizationOn as MonetizationOnIcon, CardGiftcard as CardGiftcardIcon, OpenInNew as OpenInNewIcon, CheckCircle as CheckCircleIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { Gift, Redemption } from '../types/models';
import { giftApi, redemptionApi } from '../api/apiService';
import { useUser } from '../context/UserContext';

const PointsSummaryPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user, refreshUser } = useUser();
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [selectedGift, setSelectedGift] = useState<Gift | null>(null);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [giftDetailAnchor, setGiftDetailAnchor] = useState<HTMLElement | null>(null);
  const [giftDetail, setGiftDetail] = useState<Gift | null>(null);
  const [displayedRedemptions, setDisplayedRedemptions] = useState(20);

  useEffect(() => {
    if (userId) {
      fetchGifts();
      fetchRedemptions();
    }
  }, [userId]);

  const fetchGifts = async () => {
    try {
      setLoading(true);
      const giftsResponse = await giftApi.getAll();
      setGifts(giftsResponse.data || []);
    } catch (err) {
      setError('Failed to load gifts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRedemptions = async () => {
    try {
      const response = await redemptionApi.getRedemptions(userId!);
      const sorted = ((response.data || []) as Redemption[]).slice().sort((a, b) =>
        new Date(b.redeemedAt).getTime() - new Date(a.redeemedAt).getTime()
      );
      setRedemptions(sorted);
      setDisplayedRedemptions(20);
    } catch (err) {
      console.error('Failed to load redemptions:', err);
    }
  };

  const handleRedeem = (gift: Gift) => {
    if (!user || gift.coinsRequired > user.coins) {
      setError(`Not enough coins. You have ${user?.coins || 0}, need ${gift.coinsRequired}`);
      return;
    }

    setSelectedGift(gift);
    setRedeemDialogOpen(true);
  };

  const handleGiftClick = (event: React.MouseEvent<HTMLElement>, gift: Gift) => {
    setGiftDetailAnchor(event.currentTarget);
    setGiftDetail(gift);
  };

  const handleGiftDetailClose = () => {
    setGiftDetailAnchor(null);
    setGiftDetail(null);
  };

  const confirmRedeem = async () => {
    if (!selectedGift || !userId) return;

    try {
      await redemptionApi.redeemGift(selectedGift.id!, userId);
      setRedeemDialogOpen(false);
      setSuccess(`Successfully redeemed ${selectedGift.name}!`);
      fetchGifts();
      fetchRedemptions();
      refreshUser();
    } catch (err) {
      setError('Failed to redeem gift');
      console.error(err);
    }
  };

  if (!userId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography color="text.secondary">No user selected</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Gifts Card */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <Box sx={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: 1,
              display: 'flex'
            }}>
              <CardGiftcardIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                Gifts
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Spend your coins on rewards
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${user?.coins || 0} coins`}
            sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }}
          />
        </Box>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', py: 1 }}>
                <Typography variant="h4" color="primary" fontWeight={700}>
                  {user?.totalPoints || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Total Points
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center', py: 1, borderLeft: 1, borderColor: 'divider' }}>
                <Typography variant="h4" color="secondary" fontWeight={700}>
                  {user?.coins || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Coins
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Gift List */}
      {gifts.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 4, px: 2 }}>
          <CardGiftcardIcon sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No gifts available yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create gifts from the Manage page to give this kid something to work toward.
          </Typography>
        </Card>
      ) : (
        <List>
          {gifts.map((gift) => (
            <ListItem
              key={gift.id}
              divider
              sx={{
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: 'grey.50' },
                borderRadius: 1,
                alignItems: 'flex-start',
                py: 2,
                gap: 2
              }}
              onClick={(e) => handleGiftClick(e, gift)}
            >
              {/* Left: icon */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'secondary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <CardGiftcardIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>

              {/* Middle: details */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: gift.description ? 0.5 : 0 }}>
                  {gift.name}
                </Typography>
                {gift.description && (
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.75 }}>
                    {gift.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {gift.quantity != null && (
                    <Typography variant="caption" color="text.secondary">
                      {gift.quantity} left
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MonetizationOnIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                    <Typography variant="body2" fontWeight={600} color="secondary.main">
                      {gift.coinsRequired}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Right: redeem button */}
              <Button
                variant="contained"
                size="small"
                startIcon={<RedeemIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRedeem(gift);
                }}
                disabled={(user?.coins || 0) < gift.coinsRequired}
                sx={{ alignSelf: 'center', flexShrink: 0 }}
              >
                Redeem
              </Button>
            </ListItem>
          ))}
        </List>
      )}

      {/* Redeemed Gifts */}
      {redemptions.length > 0 && (
        <Card sx={{ mt: 3, borderRadius: 3 }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                p: 1,
                display: 'flex'
              }}>
                <CheckCircleIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                  Redeemed
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {redemptions.length} gift{redemptions.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            {redemptions.slice(0, displayedRedemptions).map((redemption) => (
              <Box
                key={redemption.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  py: 1.5,
                  px: 2,
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  opacity: 0.8
                }}
              >
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary" noWrap>
                    {redemption.giftName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.disabled">
                      {new Date(redemption.redeemedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" fontWeight={600} color="secondary.main" sx={{ flexShrink: 0 }}>
                  -{redemption.pointsUsed}
                </Typography>
              </Box>
            ))}
            {redemptions.length > displayedRedemptions && (
              <Button
                fullWidth
                onClick={() => setDisplayedRedemptions(prev => prev + 20)}
                sx={{ mt: 1 }}
              >
                Load More ({redemptions.length - displayedRedemptions} more)
              </Button>
            )}
          </Box>
        </Card>
      )}

      {/* Redemption Confirmation Dialog */}
      <Dialog
        open={redeemDialogOpen}
        onClose={() => setRedeemDialogOpen(false)}
      >
        <DialogTitle>Confirm Redemption</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to redeem <strong>{selectedGift?.name}</strong> for {selectedGift?.coinsRequired} coins?
            <br /><br />
            You currently have {user?.coins} coins.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRedeemDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmRedeem} variant="contained" color="primary">
            Confirm Redemption
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gift Detail Popover */}
      <Popover
        open={Boolean(giftDetailAnchor)}
        anchorEl={giftDetailAnchor}
        onClose={handleGiftDetailClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 280, maxWidth: 320, p: 2 }
        }}
      >
        {giftDetail && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: 'secondary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <CardGiftcardIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                {giftDetail.name}
              </Typography>
            </Box>

            {giftDetail.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {giftDetail.description}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
              <MonetizationOnIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
              <Typography variant="body1" fontWeight={600} color="secondary.main">
                {giftDetail.coinsRequired}
              </Typography>
            </Box>

            {giftDetail.url && (
              <Box sx={{ mt: 1.5 }}>
                <Link
                  href={giftDetail.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    wordBreak: 'break-all'
                  }}
                >
                  {giftDetail.url}
                  <OpenInNewIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                </Link>
              </Box>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default PointsSummaryPage;